import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import Lightbox from "../components/Lightbox";
import Seo from "../components/Seo";
import { absoluteUrl } from "../lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Projects() {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${API}/gallery`).then((r) => {
      const imgs = (r.data.images || []).map((img) => ({ ...img, url: absoluteUrl(img.url) }));
      setImages(imgs);
    }).catch(() => {});
  }, []);

  const prev = () => setActive((i) => (i - 1 + images.length) % images.length);
  const next = () => setActive((i) => (i + 1) % images.length);

  return (
    <div data-testid="projects-page" className="bg-white">
      <Seo
        title="CCTV & Surveillance Projects Across Nagpur"
        description="Browse real CCTV installation and commercial surveillance projects by Hayat Security across Sitabuldi, Dharampeth, MIDC Butibori, Hingna Road, Manish Nagar and the rest of Nagpur."
        canonicalPath="/projects"
      />
      <section className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="text-sm font-semibold text-[#1C6B8A] uppercase tracking-wider">Projects</div>
            <h1 className="font-display text-4xl md:text-6xl font-bold text-slate-900 tracking-tight mt-3">
              Real installations across Nagpur
            </h1>
            <p className="text-slate-600 mt-5 text-lg">
              A glimpse into our recent CCTV, networking and surveillance projects — from shops and showrooms to warehouses and homes.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
            {images.map((img, idx) => (
              <motion.button
                key={img.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (idx % 6) * 0.06 }}
                onClick={() => setActive(idx)}
                data-testid={`gallery-item-${img.id}`}
                className="group relative overflow-hidden rounded-2xl border border-slate-200 aspect-[4/3] text-left bg-slate-900"
              >
                <img
                  src={img.url}
                  alt=""
                  aria-hidden="true"
                  className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-60"
                />
                <img
                  src={img.url}
                  alt={img.title}
                  className="relative w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all">
                  <div className="text-white font-display font-semibold text-sm">{img.title}</div>
                  {img.location && (
                    <div className="text-white/80 text-xs mt-0.5">{img.location}</div>
                  )}
                </div>
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Maximize2 className="w-4 h-4 text-slate-700" />
                </div>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      <Lightbox
        images={images}
        index={active}
        onClose={() => setActive(null)}
        onPrev={prev}
        onNext={next}
      />
    </div>
  );
}
